/**
 * Mon parcours — vue année par année (semestres S1..S6), synthèse de performance,
 * courbe d'évolution et points forts. Design sobre et professionnel (2026-10-14).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Check, X, Lock, Clock, TrendingUp, TrendingDown, Minus,
  Award, Target, GraduationCap,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi, HistoriqueAnnee, HistoriqueBlocageInfo } from '../services/portailApi';

const MENTION_LABELS: Record<string, string> = {
  tres_bien: 'Très Bien', bien: 'Bien', assez_bien: 'Assez Bien', passable: 'Passable',
};

type EvolutionPoint = { annee: string; niveau: string; semestre: number; label: string; moyenne: number; valide: boolean };
type Stats = {
  moyenne_cumulee: number; nb_semestres: number; nb_valides: number;
  meilleur_semestre: { label: string; annee: string; moyenne: number };
  semestre_faible: { label: string; annee: string; moyenne: number };
  tendance: 'hausse' | 'baisse' | 'stable'; derniere_moyenne: number;
};
type MatierePt = { nom: string; ue: string; moyenne: number; annee: string; label: string };
type PointsForts = {
  matieres_fortes: MatierePt[]; matieres_faibles: MatierePt[];
  meilleure_ue: { nom: string; moyenne: number; annee: string; label: string } | null;
};

/** Courbe d'évolution des moyennes semestrielles — SVG sobre (ligne + repère à 10). */
function EvolutionChart({ points }: { points: EvolutionPoint[] }) {
  if (points.length < 2) return null;
  const W = 320, H = 150, padX = 26, padTop = 14, padBot = 26;
  const plotW = W - padX * 2, plotH = H - padTop - padBot;
  const x = (i: number) => padX + (points.length === 1 ? plotW / 2 : (i * plotW) / (points.length - 1));
  const y = (m: number) => padTop + plotH - (Math.max(0, Math.min(20, m)) / 20) * plotH;
  const line = points.map((p, i) => `${x(i)},${y(p.moyenne)}`).join(' ');
  const yRef = y(10);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
      {/* repères horizontaux discrets */}
      {[0, 5, 15, 20].map((v) => (
        <line key={v} x1={padX} x2={W - padX} y1={y(v)} y2={y(v)} stroke="currentColor" className="text-gray-700/40" strokeWidth={1} />
      ))}
      {/* seuil de validation (10) */}
      <line x1={padX} x2={W - padX} y1={yRef} y2={yRef} stroke="currentColor" className="text-gray-500" strokeWidth={1} strokeDasharray="4 3" />
      <text x={W - padX + 2} y={yRef + 3} className="fill-gray-500" fontSize={8}>10</text>
      {/* ligne d'évolution */}
      <polyline points={line} fill="none" stroke="currentColor" className="text-indigo-400" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={x(i)} cy={y(p.moyenne)} r={3.5}
            className={p.valide ? 'fill-emerald-400' : 'fill-amber-400'} stroke="#111827" strokeWidth={1.5} />
          <text x={x(i)} y={H - 12} textAnchor="middle" className="fill-gray-400" fontSize={9}>{p.label}</text>
          <text x={x(i)} y={y(p.moyenne) - 7} textAnchor="middle" className="fill-gray-300" fontSize={8.5}>{p.moyenne.toFixed(1)}</text>
        </g>
      ))}
    </svg>
  );
}

export default function Historique() {
  const navigate = useNavigate();
  const [items, setItems] = useState<HistoriqueAnnee[]>([]);
  const [blocage, setBlocage] = useState<HistoriqueBlocageInfo | null>(null);
  const [evolution, setEvolution] = useState<EvolutionPoint[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [pointsForts, setPointsForts] = useState<PointsForts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    portailApi.historique()
      .then((r) => {
        if (!mounted) return;
        setItems(r.results);
        setBlocage(r.blocage || null);
        setEvolution((r.evolution as EvolutionPoint[]) || []);
        setStats((r.stats as Stats) || null);
        setPointsForts((r.points_forts as PointsForts) || null);
      })
      .catch((e: any) => {
        if (e.status === 401) { portailApi.clearToken(); navigate('/portail/login', { replace: true }); return; }
        if (e.status === 403) { navigate('/portail/accueil', { replace: true }); return; }
        if (mounted) setError(e?.message || "Impossible de charger le parcours. Réessayez plus tard.");
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [navigate]);

  const notesMasquees = !!blocage?.actif;

  const TendanceIcon = stats?.tendance === 'hausse' ? TrendingUp : stats?.tendance === 'baisse' ? TrendingDown : Minus;
  const tendanceColor = stats?.tendance === 'hausse' ? 'text-emerald-400' : stats?.tendance === 'baisse' ? 'text-amber-400' : 'text-gray-400';
  const tendanceLabel = stats?.tendance === 'hausse' ? 'En progression' : stats?.tendance === 'baisse' ? 'En recul' : 'Stable';

  return (
    <PortailLayout showHeader title="Mon parcours">
      <div className="space-y-3 mt-2">
        {loading ? (
          <div className="bg-gray-800/60 border border-gray-700/70 rounded-xl p-8 text-center text-gray-400 text-sm">
            Chargement de votre parcours…
          </div>
        ) : notesMasquees ? (
          <div className="bg-amber-900/15 border border-amber-700/50 rounded-xl p-3 text-sm text-amber-200 flex items-start gap-2">
            <Lock size={15} className="shrink-0 mt-0.5" />
            <span>{blocage?.message || 'Vos résultats sont temporairement masqués.'}</span>
          </div>
        ) : error ? (
          <div className="bg-gray-800/60 border border-gray-700/70 rounded-xl p-6 text-center">
            <X size={28} className="mx-auto mb-2 text-gray-500" />
            <p className="text-gray-300 text-sm">{error}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-gray-800/60 border border-gray-700/70 rounded-xl p-8 text-center">
            <BookOpen size={36} className="mx-auto mb-3 text-gray-600" />
            <p className="text-gray-400 text-sm">Aucun résultat disponible pour le moment.</p>
          </div>
        ) : (
          <>
            {/* ── Synthèse de performance ── */}
            {stats && (
              <div className="bg-gray-800/60 border border-gray-700/70 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-700/70 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-200 text-sm font-semibold">
                    <GraduationCap size={16} className="text-indigo-400" /> Synthèse du parcours
                  </div>
                  <div className={`flex items-center gap-1 text-xs font-medium ${tendanceColor}`}>
                    <TendanceIcon size={14} /> {tendanceLabel}
                  </div>
                </div>
                <div className="p-4 grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Moyenne cumulée</div>
                    <div className="text-2xl font-bold text-gray-100 tabular-nums mt-0.5">{stats.moyenne_cumulee.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Semestres validés</div>
                    <div className="text-2xl font-bold text-gray-100 tabular-nums mt-0.5">
                      {stats.nb_valides}<span className="text-gray-500 text-base">/{stats.nb_semestres}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Dernier semestre</div>
                    <div className="text-2xl font-bold text-gray-100 tabular-nums mt-0.5">{stats.derniere_moyenne.toFixed(2)}</div>
                  </div>
                </div>
                {evolution.length >= 2 && (
                  <div className="px-3 pb-3">
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 px-1 mb-1">Évolution des moyennes</div>
                    <EvolutionChart points={evolution} />
                  </div>
                )}
                {/* Points forts / à surveiller */}
                <div className="grid grid-cols-2 gap-px bg-gray-700/50 border-t border-gray-700/70 text-xs">
                  <div className="bg-gray-800/60 p-3 flex items-start gap-2">
                    <Award size={15} className="text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-gray-400">Point fort</div>
                      <div className="text-gray-200 font-medium">
                        {stats.meilleur_semestre.label} — {stats.meilleur_semestre.moyenne.toFixed(2)}
                      </div>
                      <div className="text-gray-500 text-[11px]">{stats.meilleur_semestre.annee}</div>
                    </div>
                  </div>
                  <div className="bg-gray-800/60 p-3 flex items-start gap-2">
                    <Target size={15} className="text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-gray-400">À consolider</div>
                      <div className="text-gray-200 font-medium">
                        {stats.semestre_faible.label} — {stats.semestre_faible.moyenne.toFixed(2)}
                      </div>
                      <div className="text-gray-500 text-[11px]">{stats.semestre_faible.annee}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Points forts par matière / UE ── */}
            {pointsForts && (pointsForts.matieres_fortes.length > 0 || pointsForts.meilleure_ue) && (
              <div className="bg-gray-800/60 border border-gray-700/70 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-700/70 flex items-center gap-2 text-gray-200 text-sm font-semibold">
                  <Award size={16} className="text-emerald-400" /> Vos points forts
                </div>
                {pointsForts.meilleure_ue && (
                  <div className="px-4 py-2.5 border-b border-gray-700/50 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wide text-gray-500">Meilleure UE</div>
                      <div className="text-gray-200 text-sm font-medium truncate">{pointsForts.meilleure_ue.nom}</div>
                      <div className="text-[11px] text-gray-500">{pointsForts.meilleure_ue.label} · {pointsForts.meilleure_ue.annee}</div>
                    </div>
                    <div className="text-lg font-bold tabular-nums text-emerald-400 shrink-0 pl-3">{pointsForts.meilleure_ue.moyenne.toFixed(2)}</div>
                  </div>
                )}
                {pointsForts.matieres_fortes.length > 0 && (
                  <div className="divide-y divide-gray-700/40">
                    {pointsForts.matieres_fortes.map((m, i) => (
                      <div key={i} className="px-4 py-2 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm text-gray-200 truncate">{m.nom}</div>
                          <div className="text-[11px] text-gray-500 truncate">{m.ue ? `${m.ue} · ` : ''}{m.label} · {m.annee}</div>
                        </div>
                        <div className="text-sm font-semibold tabular-nums text-emerald-400 shrink-0">{m.moyenne.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                )}
                {pointsForts.matieres_faibles.length > 0 && (
                  <div className="border-t border-gray-700/70">
                    <div className="px-4 pt-2.5 pb-1 flex items-center gap-2 text-[11px] uppercase tracking-wide text-gray-500">
                      <Target size={13} className="text-amber-400" /> À améliorer
                    </div>
                    <div className="divide-y divide-gray-700/40">
                      {pointsForts.matieres_faibles.map((m, i) => (
                        <div key={i} className="px-4 py-2 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm text-gray-300 truncate">{m.nom}</div>
                            <div className="text-[11px] text-gray-500 truncate">{m.ue ? `${m.ue} · ` : ''}{m.label} · {m.annee}</div>
                          </div>
                          <div className="text-sm font-semibold tabular-nums text-amber-400 shrink-0">{m.moyenne.toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Années ── */}
            {items.map((h) => {
              const semValides = h.semestres.filter((s) => s.valide === true).length;
              const totalSem = h.semestres.length;
              const moyennesDispo = h.semestres.map((s) => s.moyenne_semestre).filter((m): m is number => m !== null);
              const moyAnnee = moyennesDispo.length ? moyennesDispo.reduce((a, x) => a + x, 0) / moyennesDispo.length : null;
              return (
                <div key={h.annee_academique} className="bg-gray-800/60 border border-gray-700/70 rounded-xl overflow-hidden">
                  {/* En-tête année — sobre */}
                  <div className="px-4 py-3 flex items-center justify-between border-b border-gray-700/70">
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wide text-gray-500">{h.annee_academique}</div>
                      <div className="font-semibold text-gray-100 leading-tight truncate">{h.niveau} · {h.option}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{h.classe}</div>
                    </div>
                    {totalSem > 0 && (
                      <div className="text-right shrink-0 pl-3">
                        <div className="text-[10px] uppercase tracking-wide text-gray-500">Moy. annuelle</div>
                        <div className="text-xl font-bold text-gray-100 tabular-nums">
                          {moyAnnee !== null ? moyAnnee.toFixed(2) : (notesMasquees ? '🔒' : '—')}
                        </div>
                        <div className="text-[10px] text-gray-500">{semValides}/{totalSem} validé{semValides > 1 ? 's' : ''}</div>
                      </div>
                    )}
                  </div>

                  {h.semestres.length > 0 ? (
                    <div className="divide-y divide-gray-700/50">
                      {h.semestres.map((s) => {
                        const enAttente = !!s.en_attente && s.moyenne_semestre === null;
                        const masque = !!s.bloque || (notesMasquees && s.moyenne_semestre === null);
                        const bloqueParite = !!s.bloque_parite;
                        const sessionAnnulee = !!s.session_annulee;
                        const accent = enAttente ? 'border-sky-500/70' : masque ? 'border-amber-500/70'
                          : s.valide ? 'border-emerald-500/70' : 'border-amber-500/70';
                        const raisonMasque = sessionAnnulee ? 'Session ordinaire annulée'
                          : bloqueParite ? 'Semestre verrouillé' : 'Délibération en cours';
                        return (
                          <div key={s.semestre} className={`px-4 py-3 border-l-2 ${accent} flex items-center justify-between gap-3`}>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded bg-gray-700/70 text-gray-300">
                                  S{s.semestre}
                                </span>
                                <span className="font-medium text-gray-200 text-sm">Semestre {s.semestre}</span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {enAttente ? (
                                  <span className="inline-flex items-center gap-1 text-sky-400"><Clock size={11} /> En cours — pas encore délibéré</span>
                                ) : masque ? (
                                  <span className="inline-flex items-center gap-1 text-amber-400"><Lock size={11} /> {raisonMasque}</span>
                                ) : (
                                  <span className="tabular-nums">
                                    {s.credits_obtenus !== null && (<>{s.credits_obtenus}/{s.credits_requis} crédits</>)}
                                    {s.mention && <> · {MENTION_LABELS[s.mention] || s.mention}</>}
                                    {s.rang && <> · Rang {s.rang}</>}
                                  </span>
                                )}
                              </div>
                              {masque && (bloqueParite || sessionAnnulee) && s.message && (
                                <div className={`text-[11px] mt-1 leading-snug ${sessionAnnulee ? 'text-rose-300' : 'text-amber-300'}`}>{s.message}</div>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              {enAttente ? (
                                <div className="text-[11px] uppercase tracking-wide text-sky-400 flex items-center gap-1"><Clock size={13} /> En cours</div>
                              ) : masque ? (
                                <div className="text-[11px] uppercase tracking-wide text-amber-400 flex items-center gap-1"><Lock size={13} /> Masqué</div>
                              ) : (
                                <>
                                  <div className={`text-xl font-bold tabular-nums ${s.valide ? 'text-emerald-400' : 'text-amber-400'}`}>
                                    {s.moyenne_semestre !== null ? s.moyenne_semestre.toFixed(2) : '—'}
                                  </div>
                                  <div className={`text-[10px] uppercase tracking-wide flex items-center justify-end gap-0.5 ${s.valide ? 'text-emerald-400' : 'text-amber-400'}`}>
                                    {s.valide ? <><Check size={11} /> Validé</> : <><X size={11} /> Non validé</>}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-xs text-gray-500">Aucun résultat encore disponible pour cette année.</div>
                  )}
                </div>
              );
            })}
          </>
        )}

        <button
          onClick={() => navigate('/portail/accueil')}
          className="w-full py-3 bg-gray-800/60 hover:bg-gray-700/70 border border-gray-700/70 text-gray-300 rounded-xl text-sm font-medium transition-colors"
        >
          ← Retour à l'accueil
        </button>
      </div>
    </PortailLayout>
  );
}
